import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { User, UserRole, AmcAccess } from '../../types/user';
import { Formik, Form, FieldArray } from 'formik';
import * as yup from 'yup';
import { FormikTextField } from '../FormikTextField';
import { FormikDropDown } from '../FormikDropDown';
import { FormikSwitch } from '../FormikSwitch';
import { useGetAMCListQuery } from '../../services';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { Add, Delete } from '@mui/icons-material';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<User, 'id'> & { changePassword: boolean; amcAccess?: AmcAccess[] }) => void;
  user: User | null;
  amcId?: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().when({
    is: (_: string, context: any) =>
      context?.parent?.isNewUser === true || context?.parent?.changePassword === true,
    then: () =>
      yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
    otherwise: () => yup.string().optional(),
  }),
  role: yup.string().required('Role is required'),
  isActive: yup.boolean(),
  isNewUser: yup.boolean(),
  amcAccess: yup.array().of(
    yup.object({
      amcId: yup.string().required('AMC is required'),
      role: yup.string().required('Role for AMC is required'),
    })
  ).when('role', {
    is: (role: string) => [UserRole.AMC, UserRole.AMC_ADMIN, UserRole.AMC_SUPPORT].includes(role as UserRole),
    then: (schema) => schema.min(1, 'At least one AMC access is required for AMC roles'),
    otherwise: (schema) => schema.optional(),
  }),
});

export default function UserForm({
  open,
  onClose,
  onSubmit,
  user,
  amcId,
}: UserFormProps) {
  const isCreate = !user;
  const { data: amcs = [] } = useGetAMCListQuery();
  const currentUserRole = useSelector((state: RootState) => state.user.role);

  const normalizeRole = (r: any) => {
    if (!r) return '';
    const s = String(r).toLowerCase().trim();
    if (s === 'admin') return UserRole.ADMIN;
    if (s === 'support') return UserRole.SUPPORT;
    if (s === 'amc-user' || s === 'amcuser' || s === 'amc user' || s === 'amc') return UserRole.AMC;
    if (s === 'amc-admin' || s === 'amcadmin' || s === 'amc admin' || s === 'amc_admin') return UserRole.AMC_ADMIN;
    return r;
  };

  const initialValues = {
    name: user?.name || '',
    email: user?.email || '',
    isActive: user?.isActive ?? true,
    role: user?.role || '',
    password: '',
    changePassword: false,
    isNewUser: !user,
    amcAccess: user?.amcAccess?.map(a => ({
      amcId: a.amcId || (a as any).AMCId || (a as any).amc_id || '',
      role: normalizeRole(a.role || (a as any).accessRole || (a as any).amcRole || (a as any).Role) || normalizeRole(user?.role) || ''
    })) || (amcId ? [{ amcId: amcId, role: normalizeRole(user?.role) || '' }] : []),
  };

  let roleOptions = [
    { value: UserRole.ADMIN, label: 'Admin' },
    { value: UserRole.SUPPORT, label: 'Support' },
    { value: UserRole.AMC, label: 'AMC User' },
    { value: UserRole.AMC_ADMIN, label: 'AMC Admin' },
  ];

  if (currentUserRole === UserRole.AMC_ADMIN) {
    roleOptions = roleOptions.filter(
      (option) =>
        option.value === UserRole.AMC ||
        option.value === UserRole.AMC_ADMIN 
    );
  }

  const amcRoleOptions = [
    { value: UserRole.AMC, label: 'AMC User' },
    { value: UserRole.AMC_ADMIN, label: 'AMC Admin' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{user ? 'Edit User' : 'Add New User'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values) => {
          const userData = {
            name: values.name,
            email: values.email,
            isActive: values.isActive,
            role: values.role,
            changePassword: values.changePassword,
            password: values.password,
            amcAccess: values.amcAccess,
          };
          onSubmit(userData);
        }}
      >
        {({ values }) => (
          <Form>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <FormikTextField name="name" label="Full Name" />
                <FormikTextField name="email" label="Email" type="email" disabled={!!user} />
                {(!user || values.changePassword) && (
                  <FormikTextField
                    name="password"
                    label="Password"
                    type="password"
                    helperText="Minimum 8 characters"
                  />
                )}
                {!isCreate && <FormikSwitch label="Change Password" name="changePassword" />}
                <FormikDropDown name="role" label="Role" options={roleOptions} />

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  AMC Access
                </Typography>

                <FieldArray name="amcAccess">
                  {({ push, remove }) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {values.amcAccess.map((_item: AmcAccess, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            bgcolor: 'action.hover',
                            p: 2,
                            borderRadius: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <FormikDropDown
                              name={`amcAccess.${index}.amcId`}
                              label="AMC"
                              options={amcs.map((a: any) => ({
                                value: a.id,
                                label: a.name || a.id,
                              }))}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <FormikDropDown
                              name={`amcAccess.${index}.role`}
                              label="Role for this AMC"
                              options={amcRoleOptions}
                            />
                          </Box>
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            disabled={values.amcAccess.length <= 1 && isCreate}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<Add />}
                        onClick={() => push({ amcId: '', role: values.role || UserRole.AMC })}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Add AMC Access
                      </Button>
                    </Box>
                  )}
                </FieldArray>

                {!isCreate && <FormikSwitch name="isActive" label="Active" />}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {user ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
